// THE EDGE APPLIANCE ON THE CONTROL PLANE: ONE HOST'S FIELDS, ITS STATE IN
// WORDS, WHERE IT IS IN THE ADDRESS, AND HOW A ROUTED SWITCH WAS ANSWERED.
//
// Shared by Port guard (Devices › Port guard) and the internet links (Devices ›
// Traffic). The wire is the control plane's (API-CONTRACT.md, "AMENDMENTS:
// multi-tenant"; controlplane/appliance.go): GET /api/portguard/state and GET
// /api/uplinks/state list the tenant's hosts, and with ?host= answer one host's
// state in the engine's shape plus these fields. Every host is in one of six
// states, told apart, and `stale` is said apart from them:
//
//   not-reported    no beat since this control plane started (it forgets every
//                   report on a restart, until the host's next beat);
//   too-old         its agent predates the report: unknown here, NEVER read as
//                   "not set up";
//   report-refused  this control plane refused the report whole;
//   starting        its agent has not started the feature yet;
//   not-configured  its agent.yaml has no section for it;
//   reporting       the host's own reading, as its last beat carried it.
//
// Only the last two carry the engine's fields. `reported_at` is this control
// plane's receive time; a host not heard from for more than `stale_after_s`
// (90 s, three beats) is stale, and every reading it carries is old.

export const EDGE_HOST_STATES = ["not-reported", "too-old", "report-refused", "starting", "not-configured", "reporting"] as const;
export type EdgeHostState = (typeof EDGE_HOST_STATES)[number] | "unknown";

/** The fields every per-host answer and every row of the list carries. */
export interface EdgeHost {
  agent_id: string;
  /** Its hostname, else its agent id (the server's choice). */
  host: string;
  state: EdgeHostState;
  /** The server's sentence for the state ("" when it sent none). */
  why: string;
  too_old: boolean;
  stale: boolean;
  /** When this control plane last heard the host; "" when it has not since it started. */
  reported_at: string;
  /**
   * When the readings were read from the host's answer to a change made
   * through this control plane, newer than its last beat (Port guard's policy
   * and state, until the beat that carries them); "" when they are the beat's.
   */
  answered_at: string;
  /** How long a host is read as current (the per-host answer and the list both say it); null when not said. */
  stale_after_s: number | null;
  /**
   * The lists of the engine's state the host's report cut at their bounds
   * (Port guard: rules, devices, degraded; the links: links, events): [] when
   * none was; null where the report is not read, or the server did not say.
   */
  truncated: string[] | null;
}

type Obj = Record<string, unknown>;

export const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const positive = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);

/**
 * One host's fields, or null when the answer names no host. A state this
 * console does not know is "unknown"; one the server left out is read from
 * the engine's fields where they are there (a switch's answer carries them
 * and no state), and "unknown" otherwise.
 */
export function readEdgeHost(raw: unknown, staleAfterS: number | null = null): EdgeHost | null {
  if (!isObj(raw)) return null;
  const agent = str(raw.agent_id).trim();
  if (!agent) return null;
  const said = str(raw.state);
  const known = (EDGE_HOST_STATES as readonly string[]).includes(said);
  const state: EdgeHostState = known
    ? (said as EdgeHostState)
    : !said && typeof raw.configured === "boolean"
      ? raw.configured
        ? "reporting"
        : "not-configured"
      : "unknown";
  return {
    agent_id: agent,
    host: str(raw.host).trim() || agent,
    state,
    why: str(raw.why).trim(),
    too_old: raw.too_old === true || state === "too-old",
    stale: raw.stale === true,
    reported_at: str(raw.reported_at),
    answered_at: str(raw.answered_at),
    stale_after_s: positive(raw.stale_after_s) ?? staleAfterS,
    truncated: Array.isArray(raw.truncated) ? raw.truncated.filter((x): x is string => typeof x === "string" && x.trim() !== "") : null
  };
}

/** Where the server says a cut in a host's `why` (controlplane/appliance.go applianceCutWords). */
const CUT_SAID = "the host's report cut its";

/**
 * A host's report cut at its bounds, said in a sentence ("" when nothing was
 * cut): the server's own words from `why` where it said them there, else
 * from the lists it named. The engine's shape has no word for a cut, so it
 * is never left silent.
 */
export function cutWords(h: Pick<EdgeHost, "truncated" | "why">): string {
  if (!h.truncated || !h.truncated.length) return "";
  const at = h.why.indexOf(CUT_SAID);
  const said = at >= 0 ? h.why.slice(at).split(";")[0].trim() : "";
  return asSentence(
    said || `the host's report cut its ${h.truncated.join(", ")} at their bounds: the rest are not shown here (the host's own console lists them all)`
  );
}

/** Whether a host's report is read here: the engine's fields are there only in these two states. */
export function hostIsRead(h: Pick<EdgeHost, "state">): boolean {
  return h.state === "reporting" || h.state === "not-configured";
}

/** The list's counts per state, every known state named (0 when not sent). */
export function readStateCounts(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  for (const s of EDGE_HOST_STATES) out[s] = 0;
  if (!isObj(raw)) return out;
  for (const [k, v] of Object.entries(raw)) if (typeof v === "number" && Number.isFinite(v) && v >= 0) out[k] = v;
  return out;
}

/** What the feature is called in a sentence. */
export type EdgeFeature = "port-guard" | "internet-links";

const FEATURE_WORDS: Record<EdgeFeature, { name: string; report: string; section: string }> = {
  "port-guard": { name: "Port guard", report: "Port guard report", section: "port_guard" },
  "internet-links": { name: "internet links", report: "internet links report", section: "uplinks" }
};

/** The server's sentence as a sentence: its first letter up, a full stop at its end. */
function asSentence(s: string): string {
  const t = s.trim();
  if (!t) return "";
  const up = t[0].toUpperCase() + t.slice(1);
  return /[.!?]$/.test(up) ? up : `${up}.`;
}

/** A host's state, said plainly: a short label for the list, and the sentence (the server's when it sent one). */
export function hostStateWords(h: Pick<EdgeHost, "state" | "why">, feature: EdgeFeature): { label: string; sentence: string } {
  const words = stateWords({ state: h.state, why: asSentence(h.why) }, feature);
  return { label: words.label, sentence: asSentence(words.sentence) };
}

function stateWords(h: Pick<EdgeHost, "state" | "why">, feature: EdgeFeature): { label: string; sentence: string } {
  const f = FEATURE_WORDS[feature];
  switch (h.state) {
    case "not-reported":
      return {
        label: "Not reported yet",
        sentence:
          h.why ||
          "This host has not reported to this control plane since it started; a restart of the control plane forgets every report until the host's next beat, within 30 seconds."
      };
    case "too-old":
      return {
        label: "Too old to report",
        sentence: h.why || `Its agent predates the ${f.report}, so its ${f.name} state is unknown here: not read as not set up, and it is sent no switch.`
      };
    case "report-refused":
      return { label: "Report refused", sentence: h.why || `This control plane refused the host's ${f.report} whole, so none of it is shown.` };
    case "starting":
      return { label: "Starting", sentence: h.why || `The host's agent has not started ${f.name} yet (its first seconds after a start).` };
    case "not-configured":
      return { label: "Not set up", sentence: h.why || `The host's config file has no ${f.section} section.` };
    case "reporting":
      return { label: "Reporting", sentence: h.why || "The host's own reading, as its last beat carried it." };
    default:
      return { label: "State not read", sentence: h.why || "The control plane sent a state this console cannot read, so nothing is claimed about this host." };
  }
}

/** "20 s ago", "3 min ago"; "not since this control plane started" when it has not reported. */
export function reportedWords(h: Pick<EdgeHost, "reported_at">, now: number): string {
  const at = Date.parse(h.reported_at);
  if (!h.reported_at || Number.isNaN(at)) return "not since this control plane started";
  const ms = Math.max(0, now - at);
  if (ms < 5_000) return "just now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s ago`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min ago`;
  const hours = Math.floor(min / 60);
  if (hours < 48) return min % 60 ? `${hours} h ${min % 60} min ago` : `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h ago` : `${days} d ago`;
}

/** The staleness rule in words: "more than 90 s (three beats)". */
export function staleRuleWords(h: Pick<EdgeHost, "stale_after_s">): string {
  const s = h.stale_after_s;
  return s ? `more than ${s} s (${Math.max(1, Math.round(s / 30))} beats of 30 s)` : "more than three beats";
}

// ── where the host is in the address ─────────────────────────────────────

/** The host an address names: "#portguard?host=edge-01", "#traffic/links?host=edge-01". */
export function hostInHash(hash: string): string | null {
  const q = hash.indexOf("?");
  if (q < 0) return null;
  const v = new URLSearchParams(hash.slice(q + 1)).get("host")?.trim();
  return v ? v : null;
}

/** Whether the address asks for the Port guard lens: #portguard, #portguard/…, #portguard?…. */
export function portGuardHashWanted(hash: string): boolean {
  return /^#portguard(?:[/?]|$)/.test(hash);
}

/** Whether the address asks for the Internet links section of Devices › Traffic, with or without a host. */
export function linksHashWanted(hash: string): boolean {
  return /^#traffic(?:\/links)?(?:\?|$)/.test(hash);
}

/** Devices › Port guard with a host selected (or the list). */
export function portGuardHostHash(host: string | null): string {
  return host ? `#portguard?host=${encodeURIComponent(host)}` : "#portguard";
}

/** Devices › Traffic's Internet links section with a host selected (or the list). */
export function linksHostHash(host: string | null): string {
  return host ? `#traffic/links?host=${encodeURIComponent(host)}` : "#traffic/links";
}

/** Whether a row is the host an address names: by its agent id, or by its hostname (the server takes both). */
export function hostMatches(h: Pick<EdgeHost, "agent_id" | "host">, name: string | null): boolean {
  if (!name) return false;
  return h.agent_id === name || h.host === name;
}

// The host last chosen or addressed on this page, in either lens: Port guard's
// gw-b, then Traffic, opens the internet links on gw-b too where it has them.
// In memory only: a reload reads the address, which names its own host.
let lastEdgeHost: string | null = null;

/** Remember the host a lens shows (chosen, or named by the address). */
export function rememberEdgeHost(name: string | null): void {
  if (name) lastEdgeHost = name;
}

/**
 * The host a lens opens on where the address names none: the one last shown
 * in either lens where it is a candidate, else the first heard from lately,
 * else the first; null when there is no candidate.
 */
export function defaultEdgeHost<T extends Pick<EdgeHost, "agent_id" | "host" | "stale">>(candidates: readonly T[]): string | null {
  if (!candidates.length) return null;
  const last = lastEdgeHost ? candidates.find((c) => hostMatches(c, lastEdgeHost)) : undefined;
  return (last ?? candidates.find((c) => !c.stale) ?? candidates[0]).agent_id;
}

/**
 * Whether the address anchors something else in Devices › Traffic (a link's
 * readings, #traffic?iface=…; the flood guard, #traffic/flood): the links'
 * list then opens on no host, so the anchor is never replaced.
 */
export function trafficAnchored(hash: string): boolean {
  const m = /^#traffic(\/[^?]*)?(?:\?(.*))?$/.exec(hash);
  if (!m) return false;
  if (m[1] && m[1] !== "/links") return true;
  return [...new URLSearchParams(m[2] ?? "").keys()].some((k) => k !== "host");
}

// ── a switch routed to one host, as the control plane answered it ────────

export interface RoutedSwitchFailure {
  /**
   * held      waiting for a second operator (202): nothing changed yet;
   * not-sent  refused before anything left for the host (400, 403, 404, 409 sent:false, 423, 503);
   * refused   the host refused it in its own words (409 or 400 with sent:true);
   * timeout   sent, not acknowledged inside the wait (504): whether it changed is not known;
   * routing   the host said it named another agent (502): nothing claimed in effect;
   * unknown   an error answer, or none: it may have changed.
   */
  kind: "held" | "not-sent" | "refused" | "timeout" | "routing" | "unknown";
  status: number | null;
  /** A short lead, then the server's own words. */
  lead: string;
  /** The server's `error` (or detail), verbatim. */
  said: string;
  /** The held request's id, for Approvals. */
  approvalId?: string;
  /** Until when a held request can be approved, or a sent command stays valid. */
  until?: string;
  /** Whether the state should be read again at once (the answer does not say what is in force). */
  reread: boolean;
}

interface ErrorLike {
  status?: unknown;
  body?: unknown;
  message?: unknown;
}

/**
 * How a switch sent through the control plane was answered, from the ApiError
 * the request threw. The words are the server's (`error`, else `detail`),
 * shown verbatim behind a short lead that never claims more than the status
 * says.
 */
export function routedSwitchFailure(error: unknown, who: string): RoutedSwitchFailure {
  const e = (typeof error === "object" && error !== null ? error : {}) as ErrorLike;
  const status = typeof e.status === "number" && e.status > 0 ? e.status : null;
  const body = isObj(e.body) ? e.body : {};
  const message = typeof e.message === "string" ? e.message : typeof error === "string" ? error : "";
  const said = (str(body.error) || str(body.detail) || message).trim();
  const sent = typeof body.sent === "boolean" ? body.sent : null;
  if (status === 202 && (body.approval_required === true || body.status === "APPROVAL_REQUIRED")) {
    const approval = isObj(body.approval) ? body.approval : {};
    return {
      kind: "held",
      status,
      lead: `Waiting for a second operator: nothing has changed on ${who} yet.`,
      said: str(body.detail).trim() || "This change needs a second operator to approve it.",
      approvalId: str(approval.id) || undefined,
      until: str(approval.expires_at) || undefined,
      reread: false
    };
  }
  if (status === 504) {
    return {
      kind: "timeout",
      status,
      lead: `Sent to ${who}, not acknowledged in time.`,
      said: said || "the host did not acknowledge the switch inside the wait: whether it changed is not known",
      until: str(body.expires_at) || undefined,
      reread: true
    };
  }
  if (status === 502) {
    return { kind: "routing", status, lead: "Not confirmed: nothing is said to be in effect.", said, reread: true };
  }
  if (status !== null && status >= 400 && status < 500 && sent === true) {
    return { kind: "refused", status, lead: `${who} refused it.`, said, reread: false };
  }
  if (status !== null && ((status >= 400 && status < 500) || status === 503)) {
    return { kind: "not-sent", status, lead: "Not changed.", said: said || `HTTP ${status}`, reread: false };
  }
  // The host's own 500 that says itself nothing changed ("not changed: the ledger row … could not be written").
  if (status !== null && /^not changed\b/i.test(said)) {
    return { kind: "refused", status, lead: `${who} did not change it.`, said, reread: false };
  }
  return {
    kind: "unknown",
    status,
    lead:
      status === null
        ? "Not confirmed: no answer came back. The request may have reached the host; its next report shows the mode in force."
        : `Not confirmed: ${who} answered with an error. Whether it changed is shown by its next report, read now.`,
    said,
    reread: true
  };
}
